/**
 * CapabilityHistoryView — list of the current user's past assessment
 * submissions; selecting one shows the ResultsView inline. Pure
 * presentational; state owned by parent.
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronLeft, Eye, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { ResultsView } from './ResultsView';
import { ShareEmailDialog } from './ShareEmailDialog';

export const CapabilityHistoryView = ({
  submissions,
  selectedSubmission,
  shareDialogOpen,
  sharing,
  setView,
  setSelectedSubmission,
  setShareDialogOpen,
  onShareResults,
  onRefreshRecs,
  onShareSend,
}) => {
  return (
    <div data-testid="assessment-history">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          onClick={() => { setView('list'); setSelectedSubmission(null); }}
          data-testid="back-to-list"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />Back
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">My Assessment Results</h1>
          <p className="text-[#4B5563] text-sm">{submissions.length} submission(s)</p>
        </div>
      </div>

      {selectedSubmission ? (
        <Card className="border-[#E5E7EB]">
          <CardHeader className="border-b border-[#E5E7EB]">
            <Button variant="ghost" size="sm" onClick={() => setSelectedSubmission(null)} className="mb-2 text-[#002FA7]">Back to list</Button>
            <CardTitle>Assessment Result</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <ResultsView submission={selectedSubmission} onShareResults={onShareResults} onRefreshRecs={onRefreshRecs} />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {submissions.map((s) => (
            <Card key={s.submission_id} className="border-[#E5E7EB] hover:border-[#002FA7] cursor-pointer transition-colors" data-testid={`submission-${s.submission_id}`}>
              <CardContent className="p-4 flex items-center gap-4" onClick={() => setSelectedSubmission(s)}>
                <div className="w-12 h-12 rounded-full bg-[#002FA7]/10 flex items-center justify-center">
                  <span className="text-lg font-bold text-[#002FA7]">{s.overall_score}%</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-[#0A0A0A]">{s.maturity_level} Maturity</p>
                  <p className="text-xs text-[#9CA3AF]">{format(new Date(s.created_at), 'MMM d, yyyy h:mm a')}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs text-[#002FA7] border-[#002FA7]/30 mr-2"
                  onClick={(e) => { e.stopPropagation(); onShareResults(s); }}
                  data-testid={`share-result-${s.submission_id}`}
                >
                  <Mail className="w-3 h-3 mr-1" />Share
                </Button>
                <Eye className="w-4 h-4 text-[#9CA3AF]" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ShareEmailDialog
        open={shareDialogOpen}
        onClose={setShareDialogOpen}
        title="Share Results via Email"
        onSend={onShareSend}
        sending={sharing}
      />
    </div>
  );
};
